// src/components/OAuthCallback.js - Handles Supabase OAuth callbacks
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, isSupabaseConfigured } from '../../lib/supabase'
import logger from '../../services/logger'

export default function OAuthCallback() {
  const navigate = useNavigate()
  const [error, setError] = useState(null)
  const [status, setStatus] = useState('processing')

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check if Supabase is configured
      if (!isSupabaseConfigured()) {
        setError('Supabase is not configured. Please check environment variables.')
        return
      }

      try {
        setStatus('processing')
        logger.progress('Processing OAuth callback...')

        // Get the hash fragment (Supabase returns tokens in URL hash)
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')

        // Also check for code (PKCE flow)
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')

        if (code) {
          // PKCE flow - exchange code for session
          logger.success('OAuth code received, exchanging for session...')
          const { data, error } = await supabase.auth.exchangeCodeForSession(code)

          if (error) throw error

          logger.success('Session established:', data.session?.user?.email)
          setStatus('success')

          // Redirect to dashboard
          setTimeout(() => {
            navigate('/dashboard', { replace: true })
          }, 1000)

        } else if (accessToken) {
          // Direct token flow (fallback)
          logger.success('OAuth tokens received, setting session...')

          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          })

          if (error) throw error

          logger.success('Session set successfully')
          setStatus('success')

          // Redirect to dashboard
          setTimeout(() => {
            navigate('/dashboard', { replace: true })
          }, 1000)

        } else {
          // Check for error in URL
          const errorDescription = hashParams.get('error_description') || urlParams.get('error_description')
          if (errorDescription) {
            throw new Error(errorDescription)
          }

          // No token or code found
          logger.warn('No OAuth credentials found, redirecting to home...')
          setError('No authentication credentials found')
          setTimeout(() => {
            navigate('/', { replace: true })
          }, 2000)
        }
      } catch (err) {
        logger.error('OAuth callback error:', err)
        setError(err.message)
        setStatus('error')
      }
    }

    handleOAuthCallback()
  }, [navigate])

  // Error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#1a1a1a',
        color: 'white',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '500px',
          backgroundColor: '#2a2a2a',
          padding: '30px',
          borderRadius: '12px',
          border: '1px solid #ff6b6b'
        }}>
          <h2 style={{ color: '#ff6b6b', marginTop: 0 }}>❌ Authentication Error</h2>
          <p style={{ color: '#ccc', lineHeight: '1.6' }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#1db954',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              marginTop: '20px',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    )
  }

  // Success state
  if (status === 'success') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#1a1a1a',
        color: 'white'
      }}>
        <div style={{
          textAlign: 'center',
          backgroundColor: '#2a2a2a',
          padding: '30px',
          borderRadius: '12px',
          border: '1px solid #1db954'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
          <h2 style={{ color: '#1db954', margin: '0 0 10px 0' }}>Authentication Successful!</h2>
          <p style={{ color: '#ccc' }}>Redirecting to dashboard...</p>
        </div>
      </div>
    )
  }

  // Processing state
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#1a1a1a',
      color: 'white'
    }}>
      <div style={{
        textAlign: 'center',
        backgroundColor: '#2a2a2a',
        padding: '30px',
        borderRadius: '12px'
      }}>
        <div style={{
          border: '4px solid rgba(255, 255, 255, 0.1)',
          borderTop: '4px solid #1db954',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          margin: '0 auto',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '20px', color: '#ccc' }}>Completing authentication...</p>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
